import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function MiningTable5() {
  const pdfRef = useRef();

  const data = [
    {
      industry_label: "Coal Mining",
      female: 0,
      male: 0,
      male_to_female_ratio: 0.0,
    },
    {
      industry_label: "Metal Ore Mining",
      female: 348,
      male: 2105,
      male_to_female_ratio: 6.0,
    },
    {
      industry_label: "Nonmetallic Mineral Mining and Quarrying",
      female: 231,
      male: 1552,
      male_to_female_ratio: 6.7,
    },
    {
      industry_label: "Oil and Gas Extraction",
      female: 101,
      male: 901,
      male_to_female_ratio: 8.9,
    },
    {
      industry_label: "Support Activities for Mining",
      female: 644,
      male: 3544,
      male_to_female_ratio: 5.5,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.industry_label),
    datasets: [
      {
        label: "Female Employment",
        data: data.map((d) => d.female),
        backgroundColor: "rgba(236, 72, 153, 0.85)",
        borderRadius: 6,
      },
      {
        label: "Male Employment",
        data: data.map((d) => d.male),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Male to Female Ratio in 2024",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Employees" },
        beginAtZero: true,
      },
      y: {
        ticks: { font: { size: 12 } },
      },
    },
  };

  // Insights
  const totalFemale = data.reduce((sum, d) => sum + d.female, 0);
  const totalMale = data.reduce((sum, d) => sum + d.male, 0);
  const overallRatio = (totalMale / (totalFemale || 1)).toFixed(1);

  const topIndustry = data.reduce((max, d) =>
    d.male_to_female_ratio > max.male_to_female_ratio ? d : max,
  );

  const mostBalanced = data.reduce((min, d) =>
    d.male_to_female_ratio > 0 &&
    d.male_to_female_ratio < min.male_to_female_ratio
      ? d
      : min,
  );

  // PDF Export
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text("20250916.v3.198", 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );

        pdf.save("utah_mining_industry_gender_ratio.pdf");
        // input.style.width = originalWidth;
      });
    };
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Male to Female Ratio in 2024
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Male-to-Female Ratio
            </p>
            <p className="text-xl font-bold text-blue-800">
              {overallRatio} : 1
            </p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Most Male-Dominated Industry
            </p>
            <p className="text-lg font-bold text-pink-800">
              {topIndustry.industry_label} ({topIndustry.male_to_female_ratio} :
              1)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Most Gender-Balanced Industry
            </p>
            <p className="text-lg font-bold text-green-800">
              {mostBalanced.industry_label} ({mostBalanced.male_to_female_ratio}{" "}
              : 1)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex items-center">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Industry</th>
                    <th className="p-2">Female</th>
                    <th className="p-2">Male</th>
                    <th className="p-2">Male-to-Female Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr
                      key={i}
                      className={`${
                        row.industry_label === topIndustry.industry_label
                          ? "bg-red-100 font-semibold"
                          : row.industry_label === mostBalanced.industry_label
                            ? "bg-green-100 font-semibold"
                            : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{row.industry_label}</td>
                      <td className="p-2 text-pink-700">
                        {row.female.toLocaleString()}
                      </td>
                      <td className="p-2 text-blue-700">
                        {row.male.toLocaleString()}
                      </td>
                      <td className="p-2">{row.male_to_female_ratio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-center text-gray-700 mt-4">
          📊 Utah’s mining workforce remains male-dominated, particularly in Oil
          and Gas Extraction and Metal Ore Mining. However, Support Activities
          for Mining shows the most balanced gender participation at a ratio of
          5.5 to 1.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
